import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Typography, TextField, Paper, Stack, InputAdornment, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Formio } from '@formio/js';
import { useDispatch } from 'react-redux';
import { useFormio } from '../../components/formio/FormioProvider';
import { fetchFormioFormThunk, createFormioFormThunk, updateFormioFormThunk } from '../../store/thunks/formioThunks';
import { useToast } from '../common/custom-component/ToastProvider';
import LoadingBackdrop from '../common/custom-component/LoadingBackdrop';
import { logger } from 'helpers/logger';

const slugifySegment = (text) =>
    (text || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');

// Slugifies each "/"-separated segment independently, so a namespace prefix
// like "HR / Payroll" becomes "hr/payroll" instead of losing the segment
// boundary.
const slugifyPath = (value) =>
    (value || '')
        .split('/')
        .map(slugifySegment)
        .filter(Boolean)
        .join('/');

// Path is the directory (e.g. "test/forms"), formName is the leaf (e.g.
// "Test Form" -> "test-form"). Combined: "test/forms/test-form".
const joinPath = (prefix, leaf) => {
    const cleanPrefix = slugifyPath(prefix);
    const cleanLeaf = slugifySegment(leaf);
    return [cleanPrefix, cleanLeaf].filter(Boolean).join('/');
};

// "Classic" gives wizards a title+progress+dot stepper look while keeping
// every step freely clickable (unlike "StepIndicator", which disables
// clicking ahead to unvisited steps) - see Wizard.js/wizardHeaderClassic
// in @formio/js for how this was verified.
const applyWizardSettings = (schema, type) => {
    if (type !== 'wizard') return schema;
    return { ...schema, settings: { ...(schema.settings || {}), wizardHeaderType: 'Classic' } };
};

const FormBuilderComponent = ({ formId, onSave, onCancel }) => {
    const { isFormioReady } = useFormio();
    const dispatch = useDispatch();
    const { success: ToastSuccess, error: ToastError } = useToast();
    const builderRef = useRef(null);
    const formBuilderInstance = useRef(null);
    const [formName, setFormName] = useState('');
    const [formPath, setFormPath] = useState('');
    const [displayType, setDisplayType] = useState('form');
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [isEditMode, setIsEditMode] = useState(false);
    const [originalForm, setOriginalForm] = useState(null);


    useEffect(() => {
        if (formId && isFormioReady) {
            setIsEditMode(true);
            fetchFormData();
        }
    }, [formId, isFormioReady]);

    const fetchFormData = async () => {
        try {
            setLoadingMessage('Loading form...');
            setLoading(true);
            const result = await dispatch(fetchFormioFormThunk({ formId }));

            if (fetchFormioFormThunk.fulfilled.match(result)) {
                const formData = result.payload?.data || result.payload;
                setOriginalForm(formData);
                setFormName(formData.title || formData.name);
                setDisplayType(formData.display || 'form');

                // Path field holds the directory prefix only - the last segment
                // of the saved path is the leaf, which the form name re-derives.
                const pathSegments = (formData.path || '').split('/');
                setFormPath(pathSegments.slice(0, -1).join('/'));

                // Load the form into the builder
                if (formBuilderInstance.current) {
                    formBuilderInstance.current.setForm(formData);
                }
            } else {
                throw new Error(result.payload || 'Failed to fetch form');
            }
        } catch (error) {
            logger.error('Error fetching form:', error);
            ToastError('Error loading form: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let isUnmounted = false;

        if (builderRef.current && !formBuilderInstance.current) {
            const initialSchema = applyWizardSettings({ ...(originalForm || {}), display: displayType }, displayType);

            Formio.builder(builderRef.current, initialSchema, {
                noDefaultSubmitButton: false,
                builder: {
                    basic: {
                        title: 'Basic Components',
                        default: true,
                        weight: 0,
                        components: {
                            textfield: true,
                            textarea: true,
                            number: true,
                            password: true,
                            checkbox: true,
                            selectboxes: true,
                            select: true,
                            radio: true,
                            button: true,
                            email: true,
                            url: true,
                            phoneNumber: true,
                            tags: true,
                            address: true,
                            datetime: true,
                            day: true,
                            time: true,
                            currency: true,
                            survey: true,
                            signature: true
                        }
                    },
                    advanced: {
                        title: 'Advanced',
                        weight: 10,
                        components: {
                            datetime: true,
                            day: true,
                            time: true,
                            currency: true,
                            survey: true,
                            signature: true,
                            tags: true,
                            address: true
                        }
                    },
                    layout: {
                        title: 'Layout',
                        weight: 20,
                        components: {
                            htmlelement: true,
                            content: true,
                            columns: true,
                            fieldset: true,
                            panel: true,
                            table: true,
                            tabs: true,
                            well: true
                        }
                    },
                    data: {
                        title: 'Data',
                        weight: 30,
                        components: {
                            hidden: true,
                            container: true,
                            datagrid: true,
                            editgrid: true,
                            tree: true
                        }
                    }
                },
            }).then((builder) => {
                // The component may have unmounted while Formio.builder() was still
                // resolving (e.g. switching tabs quickly). If so, the effect cleanup
                // below already ran and never saw this instance, so it would be left
                // orphaned with its global drag/scroll listeners still attached.
                if (isUnmounted) {
                    builder.destroy();
                    return;
                }

                formBuilderInstance.current = builder;

                builder.on('change', (schema) => {
                    logger.info('Form schema changed:', schema);
                });

                // If in edit mode and form data loaded, set it
                if (originalForm) {
                    builder.setForm(initialSchema);
                }
            }).catch((error) => {
                logger.error('Error creating form builder:', error);
            });
        }

        return () => {
            isUnmounted = true;
            if (formBuilderInstance.current) {
                formBuilderInstance.current.destroy();
                formBuilderInstance.current = null;
            }
        };
    }, [originalForm, displayType]);

    const saveForm = async () => {
        if (!formName.trim()) {
            ToastError('Please enter a form name');
            return;
        }

        const resolvedPath = joinPath(formPath, formName);
        if (!resolvedPath) {
            ToastError('Please enter a valid form name');
            return;
        }

        if (!formBuilderInstance.current) {
            ToastError('Form builder not ready');
            return;
        }

        try {
            setLoadingMessage(isEditMode ? 'Updating form...' : 'Creating form...');
            setLoading(true);
            const schema = formBuilderInstance.current.schema;

            const formToSave = {
                title: formName,
                name: formName.toLowerCase().replace(/\s+/g, '-'),
                path: resolvedPath,
                type: 'form',
                display: displayType,
                components: schema.components || []
            };

            // If editing, preserve the original internal name (path is user-editable above)
            if (isEditMode && originalForm) {
                formToSave.name = originalForm.name;
            }

            const finalFormToSave = applyWizardSettings(formToSave, displayType);

            logger.info('Saving form:', finalFormToSave);

            const thunkAction = isEditMode ? updateFormioFormThunk : createFormioFormThunk;
            const result = isEditMode
                ? await dispatch(updateFormioFormThunk({ formId, formData: finalFormToSave }))
                : await dispatch(createFormioFormThunk({ formData: finalFormToSave }));

            logger.info('Response:', result);

            if (!thunkAction.fulfilled.match(result)) {
                throw new Error(result.payload || 'Failed to save form');
            }

            const responseData = result.payload;

            ToastSuccess(isEditMode ? 'Form updated successfully!' : 'Form created successfully!');
            if (onSave) onSave(responseData);
            
            if (!isEditMode) {
                setFormName('');
                setFormPath('');
                formBuilderInstance.current.setForm({
                    display: displayType,
                    type: 'form',
                    components: []
                });
            }
        } catch (error) {
            logger.error('Error saving form:', error);
            ToastError('Error saving form: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <LoadingBackdrop open={loading} message={loadingMessage} variant="bars" color="greenGradient" />

            <Typography variant="h4" gutterBottom>
                {isEditMode ? 'Edit Form' : 'Form Builder'}
            </Typography>

            <TextField
                label="Path"
                value={formPath}
                onChange={(e) => setFormPath(e.target.value)}
                fullWidth
                margin="normal"
                placeholder="e.g. hr/leave-request (leave empty for a top-level form)"
                helperText="Optional folder/namespace this form lives under. The form name below becomes the final segment."
                InputProps={{
                    startAdornment: <InputAdornment position="start">/</InputAdornment>,
                }}
                sx={{ mb: 2 }}
                disabled={loading}
            />

            <TextField
                label="Form Name"
                value={formName}
                onChange={(e) => setFormName(e.target.value.replace(/\//g, ''))}
                fullWidth
                margin="normal"
                placeholder="Enter form name (e.g., Restaurant Feedback)"
                helperText={`Full path: /${joinPath(formPath, formName) || '(enter a form name)'}`}
                sx={{ mb: 2 }}
                disabled={loading}
            />

            <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    Form Type
                </Typography>
                <ToggleButtonGroup
                    value={displayType}
                    exclusive
                    onChange={(e, value) => {
                        if (value) setDisplayType(value);
                    }}
                    disabled={loading}
                    size="small"
                >
                    <ToggleButton value="form">Form (single page)</ToggleButton>
                    <ToggleButton value="wizard">Wizard (multi-step)</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Paper
                elevation={3} 
                sx={{ 
                    mb: 3, 
                    height: '650px', 
                    overflow: 'hidden'
                }}
            >
                <div ref={builderRef} className="formio-theme-scope" style={{ height: '100%', width: '100%' }} />
            </Paper>
            
            <Stack direction="row" spacing={2}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={saveForm}
                    disabled={!joinPath(formPath, formName) || loading}
                    size="large"
                >
                    {loading ? 'Saving...' : (isEditMode ? 'Update Form' : 'Save Form')}
                </Button>
                
                {onCancel && (
                    <Button
                        variant="outlined"
                        onClick={onCancel}
                        disabled={loading}
                        size="large"
                    >
                        Cancel
                    </Button>
                )}
            </Stack>
        </Box>
    );
};

export default FormBuilderComponent;